import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { atom, useAtomValue, useSetAtom } from 'jotai';
import { atomFamily, useAtomCallback } from 'jotai/utils';
import { ObserverInstanceCallback, observe } from 'react-intersection-observer';

type ScrollSection = {
  id: string | null;
  itemIds: string[];
};

type ScrollSectionItem = {
  id: string;
  ref: React.RefObject<HTMLElement> | null;
  visible: boolean;
};

const sectionsAtom = atomFamily((sectionId: string | null) =>
  atom<ScrollSection>({ id: sectionId, itemIds: [] }),
);
const sectionItemsFamily = atomFamily((_combinedId: string | null) =>
  atom<ScrollSectionItem | undefined>(undefined),
);
const sectionItemValuesFamily = atomFamily((sectionId: string | null) =>
  atom(get => {
    const { itemIds } = get(sectionsAtom(sectionId));
    return itemIds.map(id => get(sectionItemsFamily(id)));
  }),
);

const CurrentSectionContext = React.createContext<string | null>(null);

export function ScrollTracking({
  sectionId,
  children,
}: {
  sectionId: string;
  children: React.ReactNode;
}) {
  return (
    <CurrentSectionContext.Provider value={sectionId}>
      {children}
    </CurrentSectionContext.Provider>
  );
}

type HTMLPropsWithoutAs = Omit<React.HTMLProps<HTMLElement>, 'as'>;

export interface ScrollTrackProps extends HTMLPropsWithoutAs {
  id: string;
  scrollIntoView?: boolean;
  defaultBehavior?: ScrollIntoViewOptions;
  rootMargin?: string;
  as?: string | React.FunctionComponent<any> | React.ComponentClass<any, any>;
}

export function ScrollTrack({
  id,
  children,
  scrollIntoView,
  defaultBehavior,
  rootMargin,
  as = 'div',
  ...props
}: ScrollTrackProps) {
  const ref = useRef<HTMLElement>(null);
  const currentSectionId = useContext(CurrentSectionContext);

  const setSections = useSetAtom(sectionsAtom(currentSectionId));
  const combinedId = useMemo(
    () => currentSectionId + id,
    [currentSectionId, id],
  );
  const item = useAtomValue(sectionItemsFamily(combinedId));
  const setItem = useSetAtom(sectionItemsFamily(combinedId));

  useEffect(() => {
    setItem({ id, ref, visible: false });
    setSections(prev => ({
      ...prev,
      itemIds: [...prev.itemIds, combinedId],
    }));

    return () => {
      setItem(undefined);
      setSections(prev => {
        return {
          ...prev,
          itemIds: prev.itemIds.filter(existing => existing !== combinedId),
        };
      });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const scrollElement = useCallback(() => {
    if (ref.current) {
      ref.current.scrollIntoView(defaultBehavior);
    }
  }, [defaultBehavior]);

  useEffect(() => {
    if (scrollIntoView && !item?.visible) {
      scrollElement();
    }
  }, [scrollIntoView, scrollElement, item]);

  const handleIntersection = useCallback<ObserverInstanceCallback>(
    (visible, _entry) => {
      setItem(prev =>
        prev
          ? prev?.visible === visible
            ? prev
            : { ...prev, id, visible }
          : prev,
      );
    },
    [setItem, id],
  );

  useEffect(() => {
    const destroy = observe(ref.current!, handleIntersection, {
      rootMargin,
    });
    return destroy;
  }, [rootMargin, handleIntersection]);

  const elementProps = React.useMemo(() => ({ ref, ...props }), [props]);

  return React.createElement(as, elementProps, children);
}

export function useScrollTrackItem(
  sectionId: string | null | undefined,
  id: string | null | undefined,
) {
  const itemId = useMemo(
    () => (sectionId && id ? sectionId + id : ''),
    [sectionId, id],
  );
  const item = useAtomValue(sectionItemsFamily(itemId));

  return sectionId && id ? item : null;
}

export function useScrollTracking(sectionId: string) {
  return useAtomCallback(
    useCallback(
      (
        get,
        _set,
        id: string,
        options?: boolean | ScrollIntoViewOptions | undefined,
      ) => {
        if (sectionId && id) {
          const item = get(sectionItemsFamily(sectionId + id));
          if (item?.ref?.current) {
            item.ref?.current?.scrollIntoView(options);
            return true;
          }
        }
        return false;
      },
      [sectionId],
    ),
  );
}

export function useScrollSectionElements(sectionId: string) {
  const items = useAtomValue(sectionItemValuesFamily(sectionId));

  return React.useMemo(() => {
    return items.map(item => [item?.id, item?.ref?.current] as const);
  }, [items]);
}

export function useScrollSectionItems(sectionId: string) {
  const items = useAtomValue(sectionItemValuesFamily(sectionId));
  return items;
}

export function useVisibleScrollSectionIds(sectionId: string) {
  const items = useAtomValue(sectionItemValuesFamily(sectionId));

  const visibleIds = items.filter(item => item?.visible).map(item => item!.id);
  const lastItemsRef = useRef<string[]>(visibleIds);

  if (JSON.stringify(visibleIds) !== JSON.stringify(lastItemsRef.current)) {
    lastItemsRef.current = visibleIds;
  }

  return lastItemsRef.current;
}

export function useScrollSectionIds(sectionId: string) {
  const items = useAtomValue(sectionItemValuesFamily(sectionId));
  const ids = items.map(item => item?.id).filter(Boolean);
  const lastItemsRef = useRef<(string | undefined)[]>(ids);

  if (JSON.stringify(ids) !== JSON.stringify(lastItemsRef.current)) {
    lastItemsRef.current = ids;
  }

  return lastItemsRef.current;
}

export function useSectionScrollTracking(
  sectionId: string,
  extraMargin: number = 10,
) {
  const elements = useScrollSectionElements(sectionId);
  const [currentId, setCurrentId] = useState<any>(undefined);

  const handleScroll = useCallback(() => {
    let nextId = undefined;
    for (const [key, section] of elements) {
      if (section) {
        const rect = (section as HTMLElement).getClientRects();
        const computedStyle = window.getComputedStyle(section as HTMLElement);
        const scrollMarginTop = parseFloat(computedStyle.scrollMarginTop) || 0;
        const marginTop = scrollMarginTop + extraMargin;

        if (rect?.[0] && rect?.[0].y - marginTop > 0) {
          break;
        }

        nextId = key;
      }
    }
    setCurrentId((currentId: any) =>
      currentId !== nextId ? nextId : currentId,
    );
  }, [elements, extraMargin]);

  return [handleScroll, currentId] as const;
}
